import React from "react";
import { useMeasure } from "react-use";

import { IconExternalLink } from "~/components/icon";
import { RefreshIntervalSelector } from "~/components/refresh-interval-selector";
import type { RefreshInterval } from "~/components/refresh-interval-selector/utils";
import { SkeletonBox } from "~/components/skeleton/box";
import { SkeletonText } from "~/components/skeleton/text";
import type { MetricTemplate } from "~/models/metric";
import { type FormatterUnit, toFormatterUnit } from "~/tools/formatter-unit";
import { ImmutableSet } from "~/tools/immutable-set";
import { useScoped } from "~/tools/impulse";
import { pluralize } from "~/tools/pluralize";
import { prop } from "~/tools/prop";
import { RemoteData } from "~/tools/remote-data";

import { SpecificRange, type TimeRange } from "../../app/state/view/timeline/TimeRange";
import { PromQLInput } from "../../metrics/PromQLInput";
import { useRefetchQueriesInNamespace } from "../../queries/use-refetch-queries-in-namespace";
import { METRICS_PATH } from "../../routes/Routes";
import { EmptyChart } from "../react/chart/chart-states/EmptyChart";
import { CopyToClipboard } from "../react/copyToClipboard/CopyToClipboard";
import { GenericErrorMessage } from "../react/error/GenericErrorMessage";
import { LabeledSwitch } from "../react/filter/LabeledSwitch";
import { LinkTo } from "../react/link";
import { Switch } from "../react/switch/Switch";
import { TimelineRangeSelectorExtended } from "../react/timeline/TimelineRangeSelectorExtended";
import { Tooltip } from "../react/tooltip/Tooltip";
import { PaginatedTimeSeriesChart } from "../TimeSeriesChart/PaginatedTimeSeriesChart";

import { QUERY_NAMESPACE } from "./_query-namespace";
import { useQueryTimeSeries } from "./_use-query-time-series";
import type { MetricsDrawerState } from "./state";

const showQueryLabel = pluralize({
	1: "Show query",
	more: (x) => `Show queries (${x})`,
});

export interface ContentProps extends Pick<MetricsDrawerState, "timestamp" | "timeRange"> {
	model: RemoteData<Error, Pick<MetricsDrawerState, "unit" | "metricQueries">>;
}

export const Content: React.FC<ContentProps> = ({ model, timeRange, timestamp }) => {
	const [measureRef, { width: chartWidth }] = useMeasure<React.ComponentRef<"div">>();
	const [isRefetching, refetchQueries] = useRefetchQueriesInNamespace(QUERY_NAMESPACE);

	const [showQueries, setShowQueries] = React.useState(false);
	const [refreshInterval, setRefreshInterval] = React.useState<RefreshInterval>("off");
	const [disabledQueries, setDisabledQueries] = React.useState<ImmutableSet<string>>(ImmutableSet.empty);

	const currentTimeRange = useScoped(timeRange);

	const enabledMetricQueries = React.useMemo(
		() =>
			model
				.transform(prop("metricQueries"))
				.getOrElse([])
				.filter(({ promql }) => !disabledQueries.has(promql)),
		[disabledQueries, model],
	);

	const result = useQueryTimeSeries({
		chartWidth,
		timeRange: currentTimeRange,
		metricQueries: enabledMetricQueries,
	});

	return (
		<div className="flex h-full w-full flex-col">
			<div className="mt-2 flex flex-wrap justify-between">
				<div className="flex-1 shrink-0">
					<DataCountInformation total={result.transform((timeSeries) => timeSeries.length).getOrElse(undefined)} />
				</div>

				<div className="ml-4 flex grow flex-wrap items-center justify-end gap-4">
					<SkeletonBox when={model.getOrElse(undefined)}>
						<LabeledSwitch
							checked={showQueries}
							className="ml-2"
							label={showQueryLabel(model.transform(prop("metricQueries")).getOrElse([]).length)}
							onChange={setShowQueries}
							size="small"
						/>
					</SkeletonBox>

					<TimelineRangeSelectorExtended contextName="metrics" disabled={result.isLoading()} timeRange={timeRange} />

					<RefreshIntervalSelector
						disabled={currentTimeRange.isSpecific() || result.isLoading()}
						loading={isRefetching}
						onChange={setRefreshInterval}
						onRefresh={refetchQueries}
						value={refreshInterval}
					/>
				</div>
			</div>

			{showQueries &&
				model
					.transform(({ metricQueries, unit }) => (
						<div className="mt-6 flex flex-col gap-2">
							{metricQueries.map(({ promql, label }, index) => (
								<QueryDetails
									alias={label}
									key={index}
									promql={promql}
									timeRange={currentTimeRange}
									unit={toFormatterUnit(unit)}
								>
									{metricQueries.length > 1 && (
										<Tooltip title={disabledQueries.has(promql) ? "Enable query" : "Disable query"}>
											<Switch
												checked={!disabledQueries.has(promql)}
												onChange={() => setDisabledQueries((current) => current.toggle(promql))}
												size="small"
											/>
										</Tooltip>
									)}
								</QueryDetails>
							))}
						</div>
					))
					.getOrElse(null)}

			<div
				className="mt-4 h-full w-full overflow-hidden rounded-xs border border-gray-300 bg-white p-4"
				ref={measureRef}
			>
				{RemoteData.all({
					unit: model.transform(prop("unit")),
					timeSeries: result,
				}).match({
					loading: () => <SkeletonBox className="h-full" />,

					failure: (failure) => <GenericErrorMessage className="h-full" error={failure.error} />,

					success: ({ value: { timeSeries, unit } }) => {
						if (timeSeries.length === 0) {
							return <EmptyChart />;
						}

						return (
							<PaginatedTimeSeriesChart
								legendMaxLines={7}
								markerTimestamp={timestamp}
								onBrush={(start, end) => timeRange.setValue(new SpecificRange(start, end))}
								pageSize={20}
								timeRange={currentTimeRange}
								timeSeries={timeSeries}
								unit={toFormatterUnit(unit)}
							/>
						);
					},
				})}
			</div>
		</div>
	);
};

const DataCountInformation: React.FC<{
	total: undefined | number;
}> = ({ total }) => (
	<SkeletonText className="flex whitespace-nowrap font-normal text-gray-800 leading-8" when={total}>
		{(x) => {
			const [prefix, count] = x === 0 ? ["There are", "no"] : ["Showing", x];

			return (
				<>
					{prefix} <span className="mx-1 font-medium">{count} time series</span> matching your criteria
				</>
			);
		}}
	</SkeletonText>
);

const QueryDetails: React.FC<{
	timeRange: TimeRange;
	promql: string;
	unit: FormatterUnit;
	alias: undefined | MetricTemplate;
	children: React.ReactNode;
}> = ({ timeRange, promql, alias, unit, children }) => (
	<div className="flex flex-row items-center gap-4">
		<CopyToClipboard className="w-full">
			<PromQLInput lint={false} readonly value={promql} />
		</CopyToClipboard>

		<Tooltip title="Open this query in the metrics explorer">
			<LinkTo
				className="flex size-[22px] items-center justify-center text-base leading-none transition-colors hover:text-primary-500"
				search={({ timestamp, detachedFilters }) => ({
					timestamp,
					timeRange,
					detachedFilters,
					metrics: {
						queries: [{ promql, alias: alias?.source }],
						settings: { unit },
					},
				})}
				target="_blank"
				to={METRICS_PATH}
			>
				<IconExternalLink />
			</LinkTo>
		</Tooltip>

		{children}
	</div>
);
